// Magic-byte image validation.
//
// Sprint 4 (audit M20): we used to trust the .ext / file.type pair when
// constructing storage paths. A user could rename `payload.svg` →
// `payload.jpg`, the upload would land in the bucket as a fake JPEG,
// and any code path that served it back with image/svg+xml (or treated
// the extension trustworthily later) was a stored-XSS waiting to fire.
//
// This helper reads the first ~16 bytes of the file and matches against
// the known signatures for the formats we accept. SVG is REJECTED at
// this layer (storefront/admin doesn't need vector images, and
// supporting them safely requires a server-side SVG sanitizer).

export type ValidatedImage = {
  /** Canonical MIME inferred from the magic bytes. */
  mime: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp' | 'image/avif';
  /** Canonical file extension matching the MIME. */
  ext: 'png' | 'jpg' | 'gif' | 'webp' | 'avif';
};

export type ValidationFailure = {
  reason:
    | 'too_large'
    | 'unknown_format'
    | 'svg_not_allowed'
    | 'read_failed';
};

const MAX_BYTES = 5 * 1024 * 1024; // 5 MiB

const MAGIC = {
  png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  jpegSoi: [0xff, 0xd8, 0xff],
  gif87a: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61],
  gif89a: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
};

function bytesStartWith(buf: Uint8Array, sig: number[]): boolean {
  if (buf.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (buf[i] !== sig[i]) return false;
  }
  return true;
}

/**
 * Validates that the file is a real raster image whose magic bytes
 * match a supported format. Returns either a {mime, ext} pair (which
 * the caller MUST use to construct the upload path/extension — never
 * trust the original file.name) or a typed failure.
 */
export async function validateImageFile(
  file: File,
): Promise<{ ok: true; data: ValidatedImage } | { ok: false; data: ValidationFailure }> {
  if (file.size > MAX_BYTES) {
    return { ok: false, data: { reason: 'too_large' } };
  }

  let head: Uint8Array;
  try {
    const slice = file.slice(0, 16);
    const arrayBuf = await slice.arrayBuffer();
    head = new Uint8Array(arrayBuf);
  } catch {
    return { ok: false, data: { reason: 'read_failed' } };
  }

  // PNG
  if (bytesStartWith(head, MAGIC.png)) {
    return { ok: true, data: { mime: 'image/png', ext: 'png' } };
  }
  // JPEG (any subtype: JFIF, EXIF — all share the FFD8FF SOI prefix)
  if (bytesStartWith(head, MAGIC.jpegSoi)) {
    return { ok: true, data: { mime: 'image/jpeg', ext: 'jpg' } };
  }
  // GIF87a / GIF89a
  if (bytesStartWith(head, MAGIC.gif87a) || bytesStartWith(head, MAGIC.gif89a)) {
    return { ok: true, data: { mime: 'image/gif', ext: 'gif' } };
  }
  // WEBP: 'RIFF' (4 bytes) + size (4 bytes) + 'WEBP'
  if (
    head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46 &&
    head[8] === 0x57 && head[9] === 0x45 && head[10] === 0x42 && head[11] === 0x50
  ) {
    return { ok: true, data: { mime: 'image/webp', ext: 'webp' } };
  }
  // AVIF / HEIF box: 4 bytes box-size + 'ftyp' + brand. 'avif' / 'avis'.
  if (
    head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70
  ) {
    const brand = String.fromCharCode(head[8] ?? 0, head[9] ?? 0, head[10] ?? 0, head[11] ?? 0);
    if (brand === 'avif' || brand === 'avis') {
      return { ok: true, data: { mime: 'image/avif', ext: 'avif' } };
    }
  }

  // Detect SVG specifically so we can give a clear error rather than a
  // generic "unknown_format". SVG is text — check for `<?xml` or `<svg`
  // case-insensitively in the first 256 bytes.
  try {
    const sniff = await file.slice(0, 256).text();
    if (/<\s*\?xml/i.test(sniff) || /<\s*svg\b/i.test(sniff)) {
      return { ok: false, data: { reason: 'svg_not_allowed' } };
    }
  } catch {
    // ignore
  }

  return { ok: false, data: { reason: 'unknown_format' } };
}

export function failureMessage(reason: ValidationFailure['reason']): string {
  switch (reason) {
    case 'too_large':
      return 'Fichier trop volumineux (max 5 Mo).';
    case 'svg_not_allowed':
      return 'Les images SVG ne sont pas autorisées.';
    case 'read_failed':
      return 'Impossible de lire le fichier.';
    case 'unknown_format':
    default:
      return "Format d'image non supporté (PNG, JPEG, GIF, WEBP, AVIF uniquement).";
  }
}
